import { GripVerticalIcon, Maximize2Icon, XIcon } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import Draggable from 'react-draggable';
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { usePrevious } from 'react-use';

import { Box, Button } from '@eluve/components';

import { AppointmentTranscriptionInSessionControls } from '../../../components/AppointmentTranscriptionInSessionControls';
import {
  useTranscriptionMachineActor,
  useTranscriptionMachineSelector,
} from '../hooks/useTranscriptionMachine';

import { AppointmentPatientDetails } from './AppointmentPatientDetails';
import { TranscriptionWaveform } from './TranscriptionWaveform';
import { TranscriptionWidgetTranscript } from './TranscriptionWidgetTranscript';

export const TranscriptionWidget = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { send } = useTranscriptionMachineActor();

  const handleDismiss = useCallback(() => {
    send({ type: 'DISMISS_SESSION' });
  }, [send]);

  const sessionAppointmentId = useTranscriptionMachineSelector(
    (state) => state.context.sessionAppointmentId,
  );

  const canDismiss = useTranscriptionMachineSelector((state) =>
    state.can({ type: 'DISMISS_SESSION' }),
  );

  const currentAppointmentPath = sessionAppointmentId
    ? `/organizations/${tenantId}/appointments/${sessionAppointmentId}`
    : null;

  const isOnAppointmentPage =
    currentAppointmentPath &&
    matchPath(currentAppointmentPath, pathname) !== null;
  const wasOnAppointmentPage = usePrevious(isOnAppointmentPage);

  useEffect(() => {
    if (wasOnAppointmentPage && !isOnAppointmentPage && canDismiss) {
      handleDismiss();
    }
  }, [wasOnAppointmentPage, isOnAppointmentPage, canDismiss, handleDismiss]);

  if (!sessionAppointmentId || !currentAppointmentPath || isOnAppointmentPage) {
    return null;
  }

  const openAppointment = () => {
    navigate(currentAppointmentPath);
  };

  return (
    <Draggable bounds="parent" handle=".move-handle">
      <Box
        vStack
        className="fixed bottom-5 right-5 z-[1000] h-[250px] w-[calc(100%-40px)] max-w-[550px] gap-0 overflow-hidden rounded-lg border bg-bg-primary shadow-xl"
      >
        <Box
          spaceBetween
          className="w-full items-center bg-bg-tertiary px-4 py-2"
        >
          <Box hStack>
            <GripVerticalIcon
              size={24}
              // eslint-disable-next-line tailwindcss/no-custom-classname
              className="move-handle hidden cursor-move md:block"
            />
            <AppointmentPatientDetails appointmentId={sessionAppointmentId} />
          </Box>
          <Box hStack>
            <Button onClick={openAppointment} size="icon" variant="outline">
              <Maximize2Icon size={24} />
            </Button>
            <Button
              disabled={!canDismiss}
              onClick={handleDismiss}
              size="icon"
              variant="outline"
            >
              <XIcon size={24} />
            </Button>
          </Box>
        </Box>
        <TranscriptionWidgetTranscript />
        <Box
          spaceBetween
          className="w-full flex-wrap-reverse items-center gap-1 bg-bg-tertiary p-2"
        >
          <TranscriptionWaveform />
          <AppointmentTranscriptionInSessionControls />
        </Box>
      </Box>
    </Draggable>
  );
};
